/** @jsxImportSource react */
import React, { ReactNode } from 'react'
import { Button } from './Button'
import { AlertCircle, CheckCircle, AlertTriangle, Info, X } from 'lucide-react'

type AlertType = 'info' | 'success' | 'warning' | 'error'

interface AlertModalProps {
  isOpen: boolean
  onClose: () => void
  title: string
  children: ReactNode
  type?: AlertType
  confirmLabel?: string
  cancelLabel?: string
  onConfirm?: () => void
  isLoading?: boolean
}

export const AlertModal: React.FC<AlertModalProps> = ({
  isOpen,
  onClose,
  title,
  children,
  type = 'info',
  confirmLabel = 'OK',
  cancelLabel,
  onConfirm,
  isLoading = false,
}) => {
  if (!isOpen) return null

  const getIcon = () => {
    switch (type) {
      case 'success':
        return <CheckCircle className="h-6 w-6 text-primary-500" />
      case 'warning':
        return <AlertTriangle className="h-6 w-6 text-secondary-500" />
      case 'error':
        return <AlertCircle className="h-6 w-6 text-secondary-900" />
      case 'info':
      default:
        return <Info className="h-6 w-6 text-primary-500" />
    }
  }

  const getBgColor = () => {
    switch (type) {
      case 'success':
        return 'bg-primary-100'
      case 'warning':
        return 'bg-secondary-100'
      case 'error':
        return 'bg-secondary-200'
      case 'info':
      default:
        return 'bg-primary-100'
    }
  }

  return (
    <div
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex justify-center items-center p-4 transition-opacity duration-300 animate-fade-in"
      aria-labelledby="modal-title"
      role="dialog"
      aria-modal="true"
      onClick={isLoading ? undefined : onClose}
    >
      <div
        className="bg-white rounded-xl text-left overflow-hidden shadow-xl transform transition-all sm:max-w-lg sm:w-full animate-fade-up"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
          <div className="sm:flex sm:items-start">
            <div
              className={`mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full ${getBgColor()} sm:mx-0 sm:h-10 sm:w-10`}
            >
              {getIcon()}
            </div>
            <div className="mt-3 text-center sm:mt-0 sm:ms-4 sm:text-left flex-1">
              <div className="flex justify-between items-center">
                <h3
                  className="text-lg leading-6 font-bold text-secondary-900"
                  id="modal-title"
                >
                  {title}
                </h3>
                <button
                  onClick={onClose}
                  disabled={isLoading}
                  className="text-secondary-400 hover:text-secondary-500 transition-colors disabled:opacity-50"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              <div className="mt-2">
                <div className="text-sm text-secondary-500">{children}</div>
              </div>
            </div>
          </div>
        </div>
        <div className="bg-secondary-50/50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse gap-2">
          <Button
            type="button"
            variant={type === 'error' ? 'danger' : 'primary'}
            isLoading={isLoading}
            disabled={isLoading}
            onClick={() => {
              if (onConfirm) onConfirm()
              else onClose()
            }}
          >
            {confirmLabel}
          </Button>
          {cancelLabel && (
            <Button type="button" variant="outline" onClick={onClose} disabled={isLoading}>
              {cancelLabel}
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}